import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./PesapalPayment.css";
import {
  initiateTransaction,
  verifyTransaction,
  updateInvoice,
  getTransaction,
} from "../api/pesapalApi";

const PesapalPayment = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoiceExists, setInvoiceExists] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [newInvoiceId, setNewInvoiceId] = useState("");
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const verificationInterval = useRef(null);
  const [formData, setFormData] = useState({
    email_address: "",
    phone_number: "",
    country_code: "KE", // Default to Kenya
    first_name: "",
    middle_name: "",
    last_name: "",
    amount: "", // For payment amount
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Reset states when invoice ID changes
    setLoading(true);
    setShowInvoiceModal(false);
    setInvoiceExists(false);
    
    const getTransactionDetails = async () => {
      try {
        const response = await getTransaction(invoiceId);

        if (response) {
          // Check if payment is already successful
          if (response.status === "success") {
            // Payment already completed, show message and redirect after delay
            setAlreadyPaid(true);
            setTimeout(() => {
              navigate(`/success/${invoiceId}`);
            }, 3000); // Redirect after 3 seconds
            return;
          }
          
          // Use functional update to avoid dependency on formData
          setFormData(prevData => ({
            ...prevData,
            amount: response.amount,
          }));
          setInvoiceExists(true);
          setShowInvoiceModal(false); // Ensure modal is closed for valid invoices
        }
      } catch (error) {
        console.log(error);
        // Show modal if invoice doesn't exist
        setShowInvoiceModal(true);
        setInvoiceError("Invoice not found. Please enter the correct invoice ID sent to your email.");
      } finally {
        if (!alreadyPaid) {
          setLoading(false);
        }
      }
    };
    getTransactionDetails();
  }, [invoiceId, navigate]);

  // Handle invoice ID submission from modal
  const handleInvoiceSubmit = (e) => {
    e.preventDefault();
    if (newInvoiceId && newInvoiceId.trim()) {
      // Reset states before navigation
      setLoading(true);
      setShowInvoiceModal(false);
      navigate(`/${newInvoiceId.trim()}`);
    }
  };

  // Clean up verification interval when component unmounts
  useEffect(() => {
    return () => {
      if (verificationInterval.current) {
        clearInterval(verificationInterval.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email_address) newErrors.email_address = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email_address))
      newErrors.email_address = "Email is invalid";

    if (!formData.phone_number)
      newErrors.phone_number = "Phone number is required";
    else if (!/^07\d{8}$/.test(formData.phone_number))
      newErrors.phone_number = "Phone number should be in format 07XXXXXXXX";

    if (!formData.first_name) newErrors.first_name = "First name is required";
    if (!formData.last_name) newErrors.last_name = "Last name is required";

    if (!formData.amount) newErrors.amount = "Amount is required";
    else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0)
      newErrors.amount = "Amount must be a positive number";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length === 0) {
      try {
        setPaymentProcessing(true);

        // Initiate transaction with Pesapal
        const response = await initiateTransaction({
          ...formData,
          invoiceId,
        });

        // Get redirect URL from response
        if (response && response.redirectUrl) {
          setRedirectUrl(response.redirectUrl);

          // Start polling for payment status
          startVerificationPolling();
        } else {
          throw new Error("Invalid response from payment gateway");
        }
      } catch (error) {
        console.error("Payment initiation failed:", error);
        setPaymentProcessing(false);
        alert("Payment initiation failed. Please try again.");
      }
    } else {
      setErrors(formErrors);
    }
  };

  const startVerificationPolling = () => {
    // Poll every 5 seconds to check payment status
    verificationInterval.current = setInterval(async () => {
      try {
        const verificationResponse = await verifyTransaction(invoiceId);

        if (verificationResponse && verificationResponse.status === "success") {
          // Payment successful, stop polling
          clearInterval(verificationInterval.current);
          setPaymentStatus("success");

          // Update invoice status
          await updateInvoice(invoiceId);

          // Redirect to success page
          navigate(`/success/${invoiceId}`);
        }
      } catch (error) {
        console.error("Error verifying payment:", error);
      }
    }, 5000);
  };

  if (loading && !alreadyPaid) {
    return (
      <div className="pesapal-payment-container">
        <div className="logo-container">
          <img src="/ihub-logo.png" alt="iHub Logo" className="ihub-logo" />
        </div>
        <div className="loading-message">Checking invoice...</div>
      </div>
    );
  }
  
  if (alreadyPaid) {
    return (
      <div className="pesapal-payment-container">
        <div className="logo-container">
          <img src="/ihub-logo.png" alt="iHub Logo" className="ihub-logo" />
        </div>
        <div className="already-paid-message">
          <h2>Payment Already Completed</h2>
          <p>This invoice has already been paid successfully.</p>
          <p>Redirecting to the success page...</p>
        </div>
      </div>
    );
  }

  // Invoice Modal
  const InvoiceModal = () => {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="logo-container">
            <img src="/ihub-logo.png" alt="iHub Logo" className="ihub-logo" />
          </div>
          <h2>Invoice Not Found</h2>
          <p>{invoiceError}</p>
          <form onSubmit={handleInvoiceSubmit} className="invoice-form">
            <div className="form-group">
              <label htmlFor="newInvoiceId">Enter Invoice ID:</label>
              <input
                type="text"
                id="newInvoiceId"
                value={newInvoiceId}
                onChange={(e) => setNewInvoiceId(e.target.value)}
                placeholder="Enter the invoice ID from your email"
                required
              />
            </div>
            <button type="submit" className="pay-button">
              Continue
            </button>
          </form>
        </div>
      </div>
    );
  };
  
  if (showInvoiceModal) {
    return <InvoiceModal />;
  }

  if (redirectUrl) {
    return (
      <div className="pesapal-payment-container">
        <div className="logo-container">
          <img src="/ihub-logo.png" alt="iHub Logo" className="ihub-logo" />
        </div>
        <h2>Complete Your Payment</h2>
        <div className="iframe-container">
          <iframe
            src={redirectUrl}
            title="Pesapal Payment"
            className="payment-iframe"
            frameBorder="0"
          />
        </div>
        {paymentStatus === "success" && (
          <div className="success-message">Payment successful! Thank you.</div>
        )}
      </div>
    );
  }

  return (
    <div className="pesapal-payment-container">
      <div className="logo-container">
        <img src="/ihub-logo.png" alt="iHub Logo" className="ihub-logo" />
      </div>

      <h2>Pay with Pesapal</h2>
      <p>All payments are processed in Kenyan Shillings (KES)</p>
      <p className="invoice-id">Invoice ID: {invoiceId}</p>

      <form onSubmit={handleSubmit} className="payment-form">
        <div className="form-group">
          <label htmlFor="first_name">First Name*</label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            className={errors.first_name ? "error" : ""}
          />
          {errors.first_name && (
            <span className="error-message">{errors.first_name}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="middle_name">Middle Name</label>
          <input
            type="text"
            id="middle_name"
            name="middle_name"
            value={formData.middle_name}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="last_name">Last Name*</label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            className={errors.last_name ? "error" : ""}
          />
          {errors.last_name && (
            <span className="error-message">{errors.last_name}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email_address">Email Address*</label>
          <input
            type="email"
            id="email_address"
            name="email_address"
            value={formData.email_address}
            onChange={handleChange}
            className={errors.email_address ? "error" : ""}
          />
          {errors.email_address && (
            <span className="error-message">{errors.email_address}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="phone_number">Phone Number*</label>
          <input
            type="text"
            id="phone_number"
            name="phone_number"
            placeholder="07XXXXXXXX"
            value={formData.phone_number}
            onChange={handleChange}
            className={errors.phone_number ? "error" : ""}
          />
          {errors.phone_number && (
            <span className="error-message">{errors.phone_number}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="country_code">Country</label>
          <select
            id="country_code"
            name="country_code"
            value={formData.country_code}
            onChange={handleChange}
            disabled
          >
            <option value="KE">Kenya</option>
          </select>
          <small>Only payments in Kenya (KES) are supported</small>
        </div>

        <div className="form-group">
          <label htmlFor="amount">Amount (KES)*</label>
          <input
            type="number"
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            disabled
          />
          {errors.amount && (
            <span className="error-message">{errors.amount}</span>
          )}
        </div>

        <button
          type="submit"
          className="pay-button"
          disabled={paymentProcessing}
        >
          {paymentProcessing ? "Processing..." : "Pay Now"}
        </button>
      </form>
    </div>
  );
};

export default PesapalPayment;
